/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Internal dependencies
 */
import { MULTIPLE_VALUE } from '../../../constants';
import { NONE, WEIGHT } from '../customConstants';
import {
  togglePrefixStyle,
  getPrefixStylesInSelection,
} from '../styleManipulation';
import { isStyle, numericToStyle, styleToNumeric } from './util';

const NORMAL_WEIGHT = 400;
const SMALLEST_BOLD = 600;
const DEFAULT_BOLD = 700;

export function weightToStyle(weight) {
  return numericToStyle(WEIGHT, weight);
}

function styleToWeight(style) {
  return styleToNumeric(WEIGHT, style);
}

function elementToStyle(element) {
  const isSpan = element.tagName.toLowerCase() === 'span';
  const fontWeight = parseInt(element.style.fontWeight);
  const hasFontWeight = fontWeight && !isNaN(fontWeight);
  if (isSpan && hasFontWeight) {
    return weightToStyle(fontWeight);
  }

  return null;
}

function stylesToCSS(styles) {
  const style = styles.find((someStyle) => isStyle(someStyle, WEIGHT));
  if (!style) {
    return null;
  }
  const fontWeight = styleToWeight(style);
  if (!fontWeight) {
    return null;
  }
  return { fontWeight };
}

// convert a set of weight styles to a set of weights
function getWeights(styles, defaultWeight = NORMAL_WEIGHT) {
  return styles.map((style) =>
    style === NONE ? defaultWeight : styleToWeight(style)
  );
}

function isBold(editorState, context) {
  const styles = getPrefixStylesInSelection(editorState, WEIGHT);
  const weights = getWeights(styles, context?.defaultFontWeight);
  const allIsBold = weights.every((w) => w >= SMALLEST_BOLD);
  return allIsBold;
}

function toggleBold(editorState, flag, defaultFontWeight = NORMAL_WEIGHT) {
  if (typeof flag === 'boolean') {
    if (flag) {
      const getDefault = () => weightToStyle(DEFAULT_BOLD);
      return togglePrefixStyle(editorState, WEIGHT, () => true, getDefault);
    }

    if (defaultFontWeight >= SMALLEST_BOLD) {
      const getNormalWeight = () => weightToStyle(NORMAL_WEIGHT);
      return togglePrefixStyle(
        editorState,
        WEIGHT,
        () => true,
        getNormalWeight
      );
    }

    return togglePrefixStyle(editorState, WEIGHT, () => false);
  }

  // if no flag is set, determine these values from current weights present

  // if any character has weight less than SMALLEST_BOLD,
  // everything should be bolded
  const shouldSetBold = (styles) =>
    getWeights(styles, defaultFontWeight).some((w) => w < SMALLEST_BOLD);

  // Removing an inline weight cannot unbold text that inherits a bold box
  // default, so materialize an explicit normal weight in that case.
  const shouldSetWeight = (styles) =>
    shouldSetBold(styles) || defaultFontWeight >= SMALLEST_BOLD;

  // if setting a bold, it should be the boldest current weight,
  // though at least DEFAULT_BOLD
  const getWeightToSet = (styles) =>
    shouldSetBold(styles)
      ? weightToStyle(
          Math.max(
            ...[DEFAULT_BOLD].concat(getWeights(styles, defaultFontWeight))
          )
        )
      : weightToStyle(NORMAL_WEIGHT);

  return togglePrefixStyle(
    editorState,
    WEIGHT,
    shouldSetWeight,
    getWeightToSet
  );
}

function getFontWeight(editorState, context) {
  const styles = getPrefixStylesInSelection(editorState, WEIGHT);
  const weights = getWeights(styles, context?.defaultFontWeight);
  if (weights.length > 1) {
    return MULTIPLE_VALUE;
  }
  return weights[0];
}

function setFontWeight(editorState, weight, defaultFontWeight = NORMAL_WEIGHT) {
  const shouldSetStyle = () => weight !== defaultFontWeight;

  // and if we're setting a style, it's the style for the weight of course
  const getBoldToSet = () => weightToStyle(weight);

  return togglePrefixStyle(editorState, WEIGHT, shouldSetStyle, getBoldToSet);
}

const formatter = {
  elementToStyle,
  stylesToCSS,
  autoFocus: true,
  getters: {
    isBold,
    fontWeight: getFontWeight,
  },
  setters: {
    toggleBold,
    setFontWeight,
  },
};

export default formatter;
